// GameBanana's public API (apiv11), read straight from the player's browser: the CS:S "Bunny Hop"
// map category, search, and a map's details and credits. Nothing goes through a server of ours,
// and map files are never fetched here: players download them from GameBanana themselves.

const API = 'https://gamebanana.com/apiv11';
/** Counter-Strike: Source > Maps > Bunny Hop. */
export const BHOP_CATEGORY = 5568;
export const BHOP_CATEGORY_URL = `https://gamebanana.com/mods/cats/${BHOP_CATEGORY}`;
const CSS_GAME = 2;

export type MapSort = 'downloads' | 'likes' | 'views' | 'newest' | 'updated';
const SORTS: Record<MapSort, string> = {
  downloads: 'Generic_MostDownloaded',
  likes: 'Generic_MostLiked',
  views: 'Generic_MostViewed',
  newest: 'Generic_Newest',
  updated: 'Generic_LatestUpdated',
};

export interface MapSummary {
  id: number;
  name: string;
  submitter: string;
  url: string;
  image: string | null;
  likes: number;
  views: number;
  added: number;
}

export interface MapDetails {
  downloads: number;
  files: { name: string; size: number; url: string }[];
  /** "Key Authors: A, B" style groups. */
  credits: { group: string; authors: string[] }[];
  /** Licence as plain text, if the author set one. */
  license: string;
}

export interface MapPage {
  maps: MapSummary[];
  total: number;
  /** More pages after this one. */
  more: boolean;
}

interface RawRecord {
  _idRow: number;
  _sName: string;
  _sProfileUrl: string;
  _tsDateAdded?: number;
  _nLikeCount?: number;
  _nViewCount?: number;
  _aSubmitter?: { _sName?: string };
  _aRootCategory?: { _sName?: string };
  _aSubCategory?: { _sName?: string };
  _aPreviewMedia?: { _aImages?: { _sBaseUrl: string; _sFile220?: string; _sFile: string }[] };
}

function summary(r: RawRecord): MapSummary {
  const img = r._aPreviewMedia?._aImages?.[0];
  return {
    id: r._idRow,
    name: r._sName,
    submitter: r._aSubmitter?._sName ?? 'unknown',
    url: r._sProfileUrl,
    image: img ? `${img._sBaseUrl}/${img._sFile220 ?? img._sFile}` : null,
    likes: r._nLikeCount ?? 0,
    views: r._nViewCount ?? 0,
    added: (r._tsDateAdded ?? 0) * 1000,
  };
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(API + path, { signal });
  if (!res.ok) throw new Error(`GameBanana answered ${res.status}`);
  return (await res.json()) as T;
}

/** A page of the Bunny Hop category. */
export async function listMaps(
  sort: MapSort,
  page: number,
  perPage = 18,
  signal?: AbortSignal,
): Promise<MapPage> {
  const q = new URLSearchParams({
    _nPage: String(page),
    _nPerpage: String(perPage),
    '_aFilters[Generic_Category]': String(BHOP_CATEGORY),
    _sSort: SORTS[sort],
  });
  const data = await get<{
    _aMetadata: { _nRecordCount: number; _bIsComplete: boolean };
    _aRecords: RawRecord[];
  }>(`/Mod/Index?${q}`, signal);
  return {
    maps: data._aRecords.map(summary),
    total: data._aMetadata._nRecordCount,
    more: !data._aMetadata._bIsComplete,
  };
}

/** Search CS:S maps by name, keeping only the Bunny Hop category. */
export async function searchMaps(text: string, page: number, signal?: AbortSignal): Promise<MapPage> {
  const q = new URLSearchParams({
    _sModelName: 'Mod',
    _sOrder: 'best_match',
    _idGameRow: String(CSS_GAME),
    _sSearchString: text,
    _nPage: String(page),
    _nPerpage: '30',
  });
  const data = await get<{
    _aMetadata: { _nRecordCount: number; _bIsComplete: boolean };
    _aRecords: RawRecord[];
  }>(`/Util/Search/Results?${q}`, signal);
  const maps = data._aRecords
    .filter((r) => r._aRootCategory?._sName === 'Maps' && r._aSubCategory?._sName === 'Bunny Hop')
    .map(summary);
  return { maps, total: maps.length, more: !data._aMetadata._bIsComplete };
}

/** HTML licence text -> plain text (the API returns the author's markup). */
function plain(html: string) {
  const d = new DOMParser().parseFromString(html, 'text/html');
  const links = [...d.querySelectorAll('a[href*="creativecommons.org/licenses"]')].map((a) =>
    a.getAttribute('href'),
  );
  const text = (d.body.textContent ?? '').replace(/\s+/g, ' ').trim();
  const cc = links[0]?.match(/licenses\/([a-z-]+)\/([\d.]+)/);
  return cc ? `CC ${cc[1].toUpperCase()} ${cc[2]}` : text.slice(0, 200);
}

const detailsCache = new Map<number, Promise<MapDetails>>();

/** Downloads, files and credits of one map (cached for the session). */
export function mapDetails(id: number): Promise<MapDetails> {
  let p = detailsCache.get(id);
  if (!p) {
    p = get<{
      _nDownloadCount?: number;
      _aFiles?: { _sFile: string; _nFilesize: number; _sDownloadUrl: string }[];
      _aCredits?: { _sGroupName: string; _aAuthors: { _sName: string }[] }[];
      _sLicense?: string;
    }>(`/Mod/${id}/ProfilePage`).then((d) => ({
      downloads: d._nDownloadCount ?? 0,
      files: (d._aFiles ?? []).map((f) => ({ name: f._sFile, size: f._nFilesize, url: f._sDownloadUrl })),
      credits: (d._aCredits ?? []).map((c) => ({
        group: c._sGroupName,
        authors: c._aAuthors.map((a) => a._sName),
      })),
      license: d._sLicense ? plain(d._sLicense) : '',
    }));
    p.catch(() => detailsCache.delete(id));
    detailsCache.set(id, p);
  }
  return p;
}
